/* GZ sections: Splide sliders, comparison slider, content tabs, copy button, product cards
   (variant switch + quick add), scroll animations, plus fallbacks for a few shared elements.
   Clean-room implementation written from the Liquid markup (see js/CONTRACT.md). */
(() => {
  const GZ = (window.GZ = window.GZ || {});
  const define = GZ.define || ((name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); });
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  const mobileMq = window.matchMedia('(max-width: 749px)');
  const num = (v, d = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : d;
  };
  const money = (cents) => (GZ.formatMoney ? GZ.formatMoney(cents) : (cents / 100).toFixed(2));
  const strings = () => window.variantStrings || { addToCart: 'Add to cart', soldOut: 'Sold out', unavailable: 'Unavailable' };
  const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());

  /* ---------------- videos inside sliders ---------------- */
  function pauseVideosIn(root, action) {
    qsa('gz-internal-video', root).forEach((wrap) => {
      const video = qs('video', wrap);
      if (!video) return;
      const mode = action || wrap.dataset.actionOnInactive || 'pause';
      if (mode === 'none') return;
      if (mode === 'mute') {
        video.muted = true;
        wrap.classList.add('internal-video--muted');
      } else {
        video.pause();
      }
    });
    qsa('video', root).forEach((v) => { if (!v.closest('gz-internal-video')) v.pause(); });
    qsa('iframe.js-youtube', root).forEach((f) => f.contentWindow && f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
    qsa('iframe.js-vimeo', root).forEach((f) => f.contentWindow && f.contentWindow.postMessage('{"method":"pause"}', '*'));
  }

  function resumeAutoplayVideosIn(root) {
    qsa('gz-internal-video[data-autoplay="true"]', root).forEach((wrap) => {
      if (wrap._userPaused) return;
      if (typeof wrap.playMuted === 'function') wrap.playMuted();
    });
  }

  /* ---------------- gz-splide-component ---------------- */
  class GzSplideComponent extends HTMLElement {
    connectedCallback() {
      this.root = qs('.splide', this);
      if (!this.root || this.splide) return;
      if (window.Splide) this.mount();
      else {
        this._wait = () => this.mount();
        window.addEventListener('load', this._wait, { once: true });
      }
    }

    disconnectedCallback() {
      if (this._wait) window.removeEventListener('load', this._wait);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this.splide) {
        try { this.splide.destroy(true); } catch (e) { /* ignore */ }
      }
      this.splide = null;
    }

    deviceOptions(device) {
      const d = this.dataset;
      const pick = (key, fallback) => {
        const v = d[`${key}${device}`];
        return v === undefined || v === '' ? fallback : v;
      };
      const type = d.type || 'slide';
      const perPage = type === 'fade' ? 1 : Math.max(1, Math.round(num(pick('slides', 1), 1)));
      const gap = num(pick('gap', 0));
      const side = type === 'fade' ? 0 : num(pick('sidePadding', 0));
      const calc = pick('paddingCalc', 'false') === 'true';
      const opts = {
        perPage,
        perMove: Math.max(1, Math.round(num(pick('perMove', 1), 1))),
        gap,
        padding: { left: side, right: side },
        fixedWidth: 0,
      };
      if (side > 0 && calc && type === 'slide' && d.focus !== 'center') {
        // first slide flush left, last slide flush right: no padding, slides narrower so the
        // next slide peeks in by the combined preview width
        opts.padding = { left: 0, right: 0 };
        opts.fixedWidth = `calc((100% - ${side * 2}px - ${gap * (perPage - 1)}px) / ${perPage})`;
      }
      return opts;
    }

    buildOptions() {
      const d = this.dataset;
      const type = ['slide', 'loop', 'fade'].includes(d.type) ? d.type : 'slide';
      const autoplay = d.autoplay === 'true';
      const options = Object.assign(
        {
          type,
          autoplay,
          interval: Math.max(1, num(d.autoplaySpeed, 5)) * 1000,
          pauseOnHover: true,
          pauseOnFocus: true,
          rewind: type === 'slide' && autoplay,
          arrows: d.arrows !== 'false',
          pagination: d.pagination !== 'false',
          drag: d.drag === 'false' ? false : d.drag === 'free' ? 'free' : true,
          omitEnd: d.omitEnd === 'true',
          speed: 500,
          keyboard: 'focused',
          mediaQuery: 'max',
        },
        this.deviceOptions('Desktop')
      );
      if (d.focus) options.focus = d.focus === 'center' ? 'center' : num(d.focus);
      if (d.trimSpace === 'false') options.trimSpace = false;
      const mobile = this.deviceOptions('Mobile');
      if (d.destroyDesktop === 'true') {
        options.destroy = true;
        mobile.destroy = false;
      } else if (d.destroyMobile === 'true') {
        mobile.destroy = true;
      }
      options.breakpoints = { 749: mobile };
      return options;
    }

    mount() {
      this._wait = null;
      if (this.splide || !window.Splide || !this.isConnected) return;
      const root = this.root;
      if (this.dataset.destroyDesktop === 'true') root.classList.add('splide--destroy-desktop');
      if (this.dataset.destroyMobile === 'true') root.classList.add('splide--destroy-mobile');
      const splide = (this.splide = new window.Splide(root, this.buildOptions()));
      const arrowsColor = this.dataset.arrowsColor;
      const dotsColor = this.dataset.dotsColor;

      // Hooks live in a Splide extension so they are re-attached whenever Splide re-mounts
      // (responsive destroy/mount clears listeners registered with splide.on()).
      const self = this;
      const GzHooks = (S) => ({
        setup() {
          S.on('arrows:mounted', (prev, next) => {
            if (arrowsColor) [prev, next].forEach((b) => b && b.classList.add(`color-${arrowsColor}`));
          });
          S.on('pagination:mounted', (data) => {
            if (!dotsColor || !data || !data.items) return;
            data.items.forEach((item) => item.button.classList.add(`color-${dotsColor}`, 'dots-custom-color'));
          });
          S.on('mounted move moved resized updated', () => self.updateHeight());
          if (self.dataset.pauseVideos !== 'false') {
            S.on('inactive', (slide) => pauseVideosIn(slide.slide));
            S.on('active', (slide) => resumeAutoplayVideosIn(slide.slide));
          }
          S.on('destroy', () => root.style.removeProperty('--active-slide-height'));
        },
      });

      try {
        splide.mount({ GzHooks });
      } catch (e) {
        console.error('[gz] splide mount failed', e);
        return;
      }
      // images loading later change slide heights
      qsa('img', root).forEach((img) => { if (!img.complete) img.addEventListener('load', () => this.updateHeight(), { once: true }); });

      if (!this._editorBound) {
        this._editorBound = true;
        this.addEventListener('shopify:block:select', (e) => {
          const s = this.splide;
          if (!s || !s.Components || !s.Components.Slides) return;
          const li = e.target.closest('.splide__slide');
          if (!li) return;
          const found = s.Components.Slides.get().find((sl) => sl.slide === li && !sl.isClone);
          if (found) s.go(found.index);
          if (s.Components.Autoplay) s.Components.Autoplay.pause();
        });
        this.addEventListener('shopify:block:deselect', () => {
          const s = this.splide;
          if (s && s.options.autoplay && s.Components.Autoplay) s.Components.Autoplay.play();
        });
      }
    }

    updateHeight() {
      const s = this.splide;
      if (!s || !s.Components || !s.Components.Slides) return;
      const adaptive = this.root.dataset.desktopAdaptiveHeight === 'true' || this.root.dataset.mobileAdaptiveHeight === 'true';
      if (!adaptive) return;
      const active = s.Components.Slides.getAt(s.index);
      if (!active) return;
      const h = active.slide.offsetHeight;
      if (h) this.root.style.setProperty('--active-slide-height', `${h}px`);
    }
  }
  define('gz-splide-component', GzSplideComponent);

  /* ---------------- gz-comparison-slider ---------------- */
  class GzComparisonSlider extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.input = qs('.comparison-slider__input', this);
      this.overlay = qs('.comparison-slider__overlay', this);
      this.line = qs('.comparison-slider__line', this);
      if (!this.input) return;
      this._bound = true;
      this.input.setAttribute('aria-label', this.input.getAttribute('aria-label') || 'Before / after');
      this.input.addEventListener('input', () => this.update());
      this.input.addEventListener('change', () => this.update());
      // drag anywhere on the image (range thumb is narrow on some browsers)
      this.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.target === this.input) return;
        this.setFromPointer(e);
        const move = (ev) => this.setFromPointer(ev);
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      });
      this.update();
    }

    setFromPointer(e) {
      const rect = this.getBoundingClientRect();
      if (!rect.width) return;
      const pct = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
      this.input.value = pct;
      this.update();
    }

    update() {
      const v = Math.min(100, Math.max(0, num(this.input.value, 50)));
      if (this.overlay) this.overlay.style.width = `${v}%`;
      if (this.line) this.line.style.left = `${v}%`;
      this.style.setProperty('--percent', `${v}%`);
    }
  }
  define('gz-comparison-slider', GzComparisonSlider);

  /* ---------------- gz-content-tabs ---------------- */
  class GzContentTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.buttons = qsa('.content-tab-button', this);
      this.tabs = qsa('.content-tab', this);
      this.tabsWrap = qs('.content-tabs__tabs', this);
      this.activeBg = qs('.content-tab-buttom__active-bg', this);
      this.buttons.forEach((btn) => {
        btn.setAttribute('role', 'tab');
        btn.setAttribute('type', 'button');
        btn.addEventListener('click', () => this.select(btn.dataset.index));
      });
      this.addEventListener('keydown', (e) => {
        if (!e.target.classList || !e.target.classList.contains('content-tab-button')) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const i = this.buttons.indexOf(e.target);
        const next = this.buttons[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.buttons.length) % this.buttons.length];
        next.focus();
        this.select(next.dataset.index);
      });
      this.addEventListener('shopify:block:select', (e) => {
        const el = e.target.closest('[data-index]');
        if (el) this.select(el.dataset.index, true);
      });
      const active = this.buttons.find((b) => b.classList.contains('content-tab-button--active')) || this.buttons[0];
      if (active) this.select(active.dataset.index, true);
      if (this.activeBg && 'ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.moveBg(false));
        this._ro.observe(this);
      }
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      this._ro = null;
      this._bound = false;
    }

    select(index, instant) {
      index = String(index);
      const wrap = this.tabsWrap;
      const startH = wrap && !instant ? wrap.offsetHeight : 0;
      this.buttons.forEach((b) => {
        const on = b.dataset.index === index;
        b.classList.toggle('content-tab-button--active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      this.tabs.forEach((t) => {
        const on = t.dataset.index === index;
        t.classList.toggle('content-tab--active', on);
        t.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (!on) pauseVideosIn(t, 'pause');
      });
      this.moveBg(!instant);
      if (wrap && !instant && startH) {
        const endH = wrap.offsetHeight;
        if (endH !== startH) {
          wrap.style.height = `${startH}px`;
          wrap.offsetHeight; // eslint-disable-line no-unused-expressions
          wrap.style.height = `${endH}px`;
          clearTimeout(this._hT);
          this._hT = setTimeout(() => wrap.style.removeProperty('height'), 350);
        }
      }
    }

    moveBg(animate) {
      const bg = this.activeBg;
      if (!bg) return;
      const btn = this.buttons.find((b) => b.classList.contains('content-tab-button--active'));
      if (!btn) return;
      if (!animate || !this._bgPlaced) bg.style.transitionProperty = 'none';
      else {
        bg.style.transitionProperty = 'left, top, width, height';
        bg.style.transitionDuration = '.4s';
      }
      bg.style.left = `${btn.offsetLeft}px`;
      bg.style.top = `${btn.offsetTop}px`;
      bg.style.width = `${btn.offsetWidth}px`;
      bg.style.height = `${btn.offsetHeight}px`;
      this._bgPlaced = true;
    }
  }
  define('gz-content-tabs', GzContentTabs);

  /* ---------------- gz-copy-button ---------------- */
  class GzCopyButton extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
      this.addEventListener('click', (e) => {
        e.preventDefault();
        this.copy();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.copy();
        }
      });
    }

    copy() {
      const text = this.dataset.content || '';
      const done = () => {
        this.dataset.success = 'true';
        clearTimeout(this._t);
        this._t = setTimeout(() => { this.dataset.success = 'false'; }, 2500);
      };
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
        ta.remove();
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    }
  }
  define('gz-copy-button', GzCopyButton);

  /* ---------------- gz-card-variant-selects ---------------- */
  const CARD_WIDTHS = [165, 360, 533, 720, 940, 1066];
  const sizedUrl = (src, w) => {
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  };

  class GzCardVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.formId = this.dataset.section;
      this.card = this.closest('.card-wrapper') || this.parentElement;
      this.addEventListener('change', () => this.onChange());
    }

    get variants() {
      if (!this._variants) {
        const json = qs('script[type="application/json"]', this);
        try { this._variants = json ? JSON.parse(json.textContent) : []; } catch (e) { this._variants = []; }
      }
      return this._variants;
    }

    selectedOptions() {
      return qsa('.product-form__input', this).map((wrap) => {
        const select = qs('select', wrap);
        if (select) return select.value;
        const checked = qs('input[type="radio"]:checked', wrap);
        return checked ? checked.value : undefined;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      // option inputs are rendered in product option order; undefined = not rendered, match anything
      const variant = this.variants.find((v) => v.options.every((val, i) => options[i] === undefined || options[i] === val)) || null;
      this.currentVariant = variant;
      this.updateInput(variant);
      this.updateButton(variant);
      if (!variant) return;
      this.updatePrice(variant);
      this.updateImage(variant);
      this.updateLinks(variant);
      if (this.dataset.updateUrl === 'true' && this.dataset.url) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      this.dispatchEvent(new CustomEvent('gz:card-variant-change', { bubbles: true, detail: { variant, formId: this.formId } }));
    }

    updateInput(variant) {
      const id = this.formId;
      qsa(`form#${CSS.escape(id)} input[name="id"], input[name="id"][form="${id}"]`).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.disabled = !variant;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateButton(variant) {
      const btn = document.getElementById(`${this.formId}-submit`);
      if (!btn || btn.closest('gz-modal-opener')) return;
      const available = !!(variant && variant.available);
      btn.toggleAttribute('disabled', !available);
      const label = qs('.main-atc__label__text', btn);
      if (label) label.textContent = !variant ? strings().unavailable : available ? strings().addToCart : strings().soldOut;
    }

    updatePrice(variant) {
      const price = this.card && qs('.card-information .price, .price', this.card);
      if (!price) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      price.classList.toggle('price--on-sale', !!onSale);
      price.classList.toggle('price--sold-out', !variant.available);
      price.classList.remove('price--no-compare');
      qsa('.price__regular .price-item--regular, .price-item--sale', price).forEach((el) => { el.textContent = money(variant.price); });
      qsa('.price__compare-price .price-item--regular', price).forEach((el) => { el.textContent = onSale ? money(variant.compare_at_price) : ''; });
      const unit = qs('.unit-price', price);
      if (unit) unit.classList.toggle('hidden', !variant.unit_price_measurement);
    }

    updateImage(variant) {
      const media = variant.featured_media || (variant.featured_image ? { preview_image: variant.featured_image } : null);
      const img = document.getElementById(`${this.formId}-image`);
      if (!img || !media) return;
      const image = media.preview_image || media;
      const src = image.src;
      if (!src) return;
      const width = image.width || 1066;
      const srcset = CARD_WIDTHS.filter((w) => w <= width).map((w) => `${sizedUrl(src, w)} ${w}w`);
      srcset.push(`${sizedUrl(src, width)} ${width}w`);
      img.srcset = srcset.join(', ');
      img.src = sizedUrl(src, Math.min(533, width));
      if (image.width && image.height) {
        img.width = image.width;
        img.height = image.height;
      }
      if (image.alt != null) img.alt = image.alt;
    }

    updateLinks(variant) {
      const base = this.dataset.url;
      if (!base || !this.card) return;
      qsa('a[href]', this.card).forEach((a) => {
        const href = a.getAttribute('href');
        if (href.split('?')[0] === base) a.setAttribute('href', `${base}?variant=${variant.id}`);
      });
      qsa('[data-product-url]', this.card).forEach((el) => el.setAttribute('data-product-url', `${base}?variant=${variant.id}`));
    }
  }
  define('gz-card-variant-selects', GzCardVariantSelects);

  /* ---------------- gz-quick-add-modal ---------------- */
  // Modals are moved to a body-level `.gz-scope` host so position:fixed is not trapped by
  // transformed ancestors (sliders) while the scoped CSS still applies.
  function modalHost() {
    let host = document.getElementById('gz-modal-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'gz-modal-host';
      host.className = 'gz-scope';
      host.style.cssText = 'background:none;';
      document.body.appendChild(host);
    }
    return host;
  }

  function setInnerHTMLWithScripts(el, html) {
    el.innerHTML = html;
    qsa('script', el).forEach((old) => {
      const s = document.createElement('script');
      Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.appendChild(document.createTextNode(old.innerHTML));
      old.parentNode.replaceChild(s, old);
    });
  }

  // Section renders requested by core for a quick-add product contain the original ids;
  // append a renamed copy so lookups by `quickadd-<id>` also resolve.
  if (GZ.fetchSection && !GZ._quickAddFetchPatched) {
    GZ._quickAddFetchPatched = true;
    const original = GZ.fetchSection;
    GZ.fetchSection = (url) =>
      original(url).then((doc) => {
        const open = qsa('#gz-modal-host gz-quick-add-modal[open]').find((m) => m.originalSection);
        if (!open) return doc;
        const sid = open.originalSection;
        if (!String(url).includes(`section_id=${sid}`)) return doc;
        const copy = doc.createElement('div');
        copy.hidden = true;
        copy.innerHTML = doc.body.innerHTML.split(sid).join(`quickadd-${sid}`);
        doc.body.appendChild(copy);
        return doc;
      });
  }

  class GzQuickAddModal extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.content = qs('[id^="QuickAddInfo-"]', this);
      const close = qs('[id^="ModalClose-"]', this);
      if (close) close.addEventListener('click', () => this.hide(false));
      this.addEventListener('keyup', (e) => { if (e.code && e.code.toUpperCase() === 'ESCAPE') this.hide(); });
      this.addEventListener('click', (e) => { if (e.target === this) this.hide(); });
      const host = modalHost();
      if (this.parentElement !== host) {
        if (this.id) qsa(`#${CSS.escape(this.id)}`, host).forEach((stale) => stale !== this && stale.remove());
        host.appendChild(this); // triggers disconnect/connect; _bound guards re-init
      }
    }

    show(opener) {
      if (!this.content) return;
      this.openedBy = opener;
      const url = opener && opener.getAttribute('data-product-url');
      if (!url) return;
      const spinner = opener && qs('.loading-overlay__spinner, .loading__spinner', opener);
      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const el = this.extractProduct(doc);
          if (!el) {
            window.location.href = url;
            return;
          }
          setInnerHTMLWithScripts(this.content, el.outerHTML);
          if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
          if (window.ProductModel && window.ProductModel.loadShopifyXR) window.ProductModel.loadShopifyXR();
          this.open();
        })
        .catch((e) => {
          console.error('[gz] quick add failed', e);
          window.location.href = url;
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    extractProduct(doc) {
      let el = doc.querySelector('section[id^="MainProduct-"]');
      let sid = '';
      if (el) {
        const info = el.querySelector('gz-product-info[data-section], product-info[data-section]');
        sid = info ? info.dataset.section : el.id.replace('MainProduct-', '');
      } else {
        el = doc.querySelector('gz-product-info, product-info');
        if (!el) return null;
        sid = el.dataset.section || '';
      }
      qsa('gz-pickup-availability, pickup-availability, gz-product-modal, product-modal, gz-modal-dialog, modal-dialog, gz-sticky-atc, .sticky-atc', el).forEach((n) => n.remove());
      qsa('gz-variant-selects, variant-selects, gz-product-info, product-info', el).forEach((n) => n.setAttribute('data-update-url', 'false'));
      const gallery = el.querySelector('[id^="Slider-Gallery"]');
      if (gallery) {
        gallery.setAttribute('role', 'presentation');
        qsa('[id^="Slide-"]', gallery).forEach((li) => li.setAttribute('role', 'presentation'));
      }
      this.originalSection = sid || null;
      if (sid) {
        const newId = `quickadd-${sid}`;
        el.innerHTML = el.innerHTML.split(sid).join(newId);
        Array.from(el.attributes).forEach((a) => {
          if (a.value.includes(sid)) el.setAttribute(a.name, a.value.split(sid).join(newId));
        });
        qsa('[data-section]', el).forEach((n) => {
          if (n.dataset.section === newId) n.dataset.originalSection = sid;
        });
      }
      return el;
    }

    open() {
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      const dialog = qs('[role="dialog"]', this);
      if (window.trapFocus && dialog) window.trapFocus(this, dialog);
      if (window.pauseAllMedia) window.pauseAllMedia();
    }

    hide(preventFocus = false) {
      if (!this.hasAttribute('open')) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && cart.setActiveElement && this.openedBy) cart.setActiveElement(this.openedBy);
      if (this.content) this.content.innerHTML = '';
      this.originalSection = null;
      if (preventFocus) this.openedBy = null;
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      if (window.removeTrapFocus) window.removeTrapFocus(this.openedBy);
      if (window.pauseAllMedia) window.pauseAllMedia();
    }
  }
  define('gz-quick-add-modal', GzQuickAddModal);

  /* ---------------- section ATC buttons (add the main product) ---------------- */
  // Buttons `.main-product-atc` placed in content sections (comparison slider, tabs, columns,
  // slideshow...) submit the main product form of the page. Buttons inside the main product
  // section / product forms are left to core.
  if (!GZ._sectionAtcBound) {
    GZ._sectionAtcBound = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('.main-product-atc');
      if (!btn || btn.disabled || btn.hasAttribute('form')) return;
      if (btn.closest('[id^="MainProduct-"], gz-product-form, gz-sticky-atc, gz-product-info')) return;
      const form = qs('[id^="MainProduct-"] gz-product-form form') || qs('gz-product-info gz-product-form form');
      if (!form) return;
      e.preventDefault();
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
  }

  /* ---------------- scroll animations ---------------- */
  const indexFromClass = (el) => {
    const list = Array.from(el.classList);
    const desktop = list.map((c) => c.match(/^desktop-index-(\d+)$/)).find(Boolean);
    const base = list.map((c) => c.match(/^index-(\d+)$/)).find(Boolean);
    if (desktop && !mobileMq.matches) return desktop[1];
    return base ? base[1] : null;
  };

  function applyIndexes(section) {
    qsa('.animate-item--child', section).forEach((el) => {
      if (el.style.getPropertyValue('--index') && !el.dataset.gzIndex) return;
      const i = indexFromClass(el);
      if (i !== null) {
        el.style.setProperty('--index', i);
        el.dataset.gzIndex = i;
      }
    });
  }

  function reveal(section) {
    section.classList.remove('animate--hidden');
    section.classList.add('animate--shown');
  }

  let animObserver = null;
  function initAnimations(root = document) {
    const sections = qsa('.animate-section', root);
    if (root !== document && root.classList && root.classList.contains('animate-section')) sections.push(root);
    sections.forEach(applyIndexes);
    const hidden = sections.filter((s) => s.classList.contains('animate--hidden'));
    if (!hidden.length) return;
    if (designMode() || !('IntersectionObserver' in window)) {
      hidden.forEach(reveal);
      return;
    }
    if (!animObserver) {
      animObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            // reveal when visible, or when the page jumped past it (anchor / restored scroll)
            if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return;
            reveal(entry.target);
            obs.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0 }
      );
    }
    hidden.forEach((s) => {
      const rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight) reveal(s);
      else animObserver.observe(s);
    });
  }

  if (!GZ._sectionsDocBound) {
    GZ._sectionsDocBound = true;
    onReady(() => initAnimations());
    document.addEventListener('shopify:section:load', (e) => initAnimations(e.target));
    mobileMq.addEventListener && mobileMq.addEventListener('change', () => qsa('.animate-section').forEach(applyIndexes));
  }

  /* ---------------- fallbacks for shared elements ----------------
     Defined only if no other GZ file registered them by DOMContentLoaded (all deferred scripts
     have run by then), so they never clash with a dedicated implementation. */

  class GzInternalVideoFallback extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.video = qs('video', this);
      if (!this.video) return;
      this._bound = true;
      const v = this.video;
      this.autoplay = this.dataset.autoplay === 'true';
      const playBtn = qs('.internal-video__play', this);
      const soundBtn = qs('.internal-video__sound-btn', this);
      this.timeline = qs('.internal-video__timeline', this);

      v.addEventListener('play', () => this.classList.add('internal-video--playing'));
      v.addEventListener('pause', () => this.classList.remove('internal-video--playing'));
      v.addEventListener('ended', () => this.classList.remove('internal-video--playing'));
      v.addEventListener('volumechange', () => this.classList.toggle('internal-video--muted', v.muted));
      v.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      ['playing', 'canplay', 'pause'].forEach((ev) => v.addEventListener(ev, () => this.classList.remove('internal-video--loading')));
      v.addEventListener('timeupdate', () => {
        if (this.timeline && v.duration) this.timeline.style.setProperty('--completed', `${(v.currentTime / v.duration) * 100}%`);
      });

      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.loadSources();
          if (this.autoplay && v.muted && this.dataset.noPlayBtn !== 'true' && !this._unmutedOnce) {
            this._unmutedOnce = true;
            v.muted = false;
            v.currentTime = 0;
            this._userPaused = false;
            v.play().catch(() => {});
            return;
          }
          if (v.paused) {
            this._userPaused = false;
            v.play().catch(() => {});
          } else {
            this._userPaused = true;
            v.pause();
          }
        });
      }
      if (soundBtn) {
        soundBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          v.muted = !v.muted;
          if (!v.muted && v.paused) v.play().catch(() => {});
        });
      }
      if (this.timeline) {
        const seek = (ev) => {
          const r = this.timeline.getBoundingClientRect();
          if (!r.width || !v.duration) return;
          v.currentTime = Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)) * v.duration;
        };
        this.timeline.addEventListener('pointerdown', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          seek(ev);
          const move = (m) => seek(m);
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        });
      }

      if (this.autoplay) {
        v.muted = true;
        if ('IntersectionObserver' in window) {
          this._io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this._userPaused && this.isInActiveSlide()) this.playMuted();
              } else if (!v.paused) {
                v.pause();
              }
            });
          }, { rootMargin: '200px 0px' });
          this._io.observe(this);
        } else {
          this.playMuted();
        }
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = null;
      this._bound = false;
    }

    isInActiveSlide() {
      const slide = this.closest('.splide__slide');
      const root = slide && slide.closest('.splide');
      if (!slide || !root || !root.classList.contains('is-initialized')) return true;
      return slide.classList.contains('is-visible') || slide.classList.contains('is-active');
    }

    loadSources() {
      if (this._loaded) return;
      this._loaded = true;
      let changed = false;
      qsa('source[data-src]', this.video).forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
        changed = true;
      });
      if (changed) this.video.load();
    }

    playMuted() {
      if (!this.video) return;
      this.loadSources();
      if (!this._unmutedOnce) this.video.muted = true;
      this.video.play().catch(() => {});
    }
  }

  class GzModalOpenerFallback extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      const button = qs('button', this);
      if (!button) return;
      this._bound = true;
      button.addEventListener('click', (e) => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal && typeof modal.show === 'function') {
          e.preventDefault();
          modal.show(button);
        }
      });
    }
  }

  class GzProductRecommendationsFallback extends HTMLElement {
    connectedCallback() {
      if (this._bound || !this.dataset.url) return;
      this._bound = true;
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const all = qsa('gz-product-recommendations', doc);
            const fresh = (this.id && doc.getElementById(this.id))
              || all.find((el) => !!el.dataset.isUpsellBlock === !!this.dataset.isUpsellBlock)
              || all[0];
            if (fresh && fresh.innerHTML.trim().length) {
              setInnerHTMLWithScripts(this, fresh.innerHTML);
              this.classList.add('product-recommendations--loaded');
              initAnimations(this.closest('.animate-section') || this);
            }
          })
          .catch((e) => console.error('[gz] recommendations failed', e));
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }

  onReady(() => {
    define('gz-internal-video', GzInternalVideoFallback);
    define('gz-modal-opener', GzModalOpenerFallback);
    define('gz-product-recommendations', GzProductRecommendationsFallback);
    /* global SliderComponent, SlideshowComponent, DeferredMedia */
    try {
      if (typeof SlideshowComponent === 'function') define('gz-slideshow-component', class extends SlideshowComponent {});
      if (typeof SliderComponent === 'function') define('gz-slider-component', class extends SliderComponent {});
      if (typeof DeferredMedia === 'function') define('gz-deferred-media', class extends DeferredMedia {});
    } catch (e) {
      console.warn('[gz] Dawn slider fallback unavailable', e);
    }
  });
})();
